<script setup>
import { ref, onMounted, watch } from 'vue'
import axios from 'axios'
import Header from './component/header.vue'

const loading   = ref(false)
const saving    = ref(false)
const vouchers  = ref({ data: [], current_page: 1, last_page: 1, total: 0 })
const summary   = ref({ total_draft: 0, total_pending: 0, total_approved: 0, total_paid: 0 })
const statusTab = ref('')
const search    = ref('')
const page      = ref(1)

const showModal   = ref(false)
const modalMode   = ref('create') // create | view
const selected    = ref(null)
const formError   = ref('')

const vendors  = ref([])
const accounts = ref([])

const blankForm = () => ({
  vendor_id: '',
  description: '',
  reference_no: '',
  invoice_number: '',
  invoice_date: '',
  transaction_date: new Date().toISOString().slice(0, 10),
  debit_account_id: '',
  invoice_amount: '',
  notes: '',
  deductions: [],
})
const form = ref(blankForm())

const load = async () => {
  try {
    loading.value = true
    const params = { per_page: 20, page: page.value }
    if (statusTab.value) params.status = statusTab.value
    if (search.value)    params.search = search.value
    const res = await axios.get('/accounting/payment-vouchers', { params })
    vouchers.value = res.data.data.vouchers
    summary.value  = res.data.data.summary
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

const loadMeta = async () => {
  try {
    const [v, a] = await Promise.all([
      axios.get('/procurement/vendors', { params: { per_page: 200 } }),
      axios.get('/accounting/active-ledgers'),
    ])
    vendors.value  = v.data.data?.vendors?.data ?? v.data.data?.data ?? []
    const raw = a.data.data ?? {}
    accounts.value = (Array.isArray(raw) ? raw : Object.values(raw).flat()).filter(Boolean)
  } catch (e) {
    console.error('loadMeta failed', e)
  }
}

watch(statusTab, () => { page.value = 1; load() })
watch(page, load)
let st = null
const onSearch = () => { clearTimeout(st); st = setTimeout(() => { page.value = 1; load() }, 350) }

onMounted(() => { load(); loadMeta() })

const openCreate = () => {
  modalMode.value = 'create'
  form.value = blankForm()
  formError.value = ''
  showModal.value = true
}

const openView = async (id) => {
  try {
    const res = await axios.get(`/accounting/payment-vouchers/${id}`)
    selected.value  = res.data.data
    modalMode.value = 'view'
    showModal.value = true
  } catch (e) { console.error(e) }
}

const addDeduction = () => form.value.deductions.push({ deduction_name: '', rate: 0, amount: 0 })
const removeDeduction = (i) => form.value.deductions.splice(i, 1)

const recalcDeduction = (d) => {
  if (form.value.invoice_amount && d.rate) {
    d.amount = ((parseFloat(form.value.invoice_amount) * parseFloat(d.rate)) / 100).toFixed(2)
  }
}
const recalcAllDeductions = () => form.value.deductions.forEach(recalcDeduction)

const totalDeductions = () => form.value.deductions.reduce((s, d) => s + parseFloat(d.amount || 0), 0)
const amountPayable   = () => (parseFloat(form.value.invoice_amount || 0) - totalDeductions()).toFixed(2)

const saveVoucher = async () => {
  if (!form.value.description || !form.value.transaction_date || !form.value.invoice_amount || !form.value.debit_account_id) {
    formError.value = 'Description, date, amount and debit account are required.'
    return
  }
  try {
    saving.value = true
    formError.value = ''
    await axios.post('/accounting/payment-vouchers', form.value)
    showModal.value = false
    load()
  } catch (e) {
    formError.value = e.response?.data?.message ?? 'Failed to save voucher'
  } finally {
    saving.value = false
  }
}

const submitForApproval = async (id) => {
  if (!confirm('Submit this voucher for approval?')) return
  await axios.post(`/accounting/payment-vouchers/${id}/submit`)
  load()
  showModal.value = false
}

const approve = async (id) => {
  if (!confirm('Approve this payment voucher?')) return
  await axios.post(`/accounting/payment-vouchers/${id}/approve`)
  load()
  showModal.value = false
}

const markPaid = async (id) => {
  if (!confirm('Mark as paid? This will post a journal entry (Dr AP / Cr Cash + Cr WHT Payable).')) return
  await axios.post(`/accounting/payment-vouchers/${id}/pay`)
  load()
  showModal.value = false
}

const deleteVoucher = async (id) => {
  if (!confirm('Delete this voucher?')) return
  await axios.delete(`/accounting/payment-vouchers/${id}`)
  load()
}

const statusLabel = (s) => ({ draft: 'Draft', pending_approval: 'Pending', approved: 'Approved', paid: 'Paid', cancelled: 'Cancelled' }[s] ?? s)
const statusClass = (s) => ({ draft: 'badge--grey', pending_approval: 'badge--orange', approved: 'badge--blue', paid: 'badge--green', cancelled: 'badge--red' }[s] ?? '')

const fmt = (v) => Number(v ?? 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })
</script>

<template>
  <div>
    <Header />
    <div class="pv-page">

      <!-- Summary chips -->
      <div class="pv-chips">
        <div class="pv-chip pv-chip--grey">
          <span class="chip-label">Draft</span>
          <span class="chip-val">{{ summary.total_draft }}</span>
        </div>
        <div class="pv-chip pv-chip--orange">
          <span class="chip-label">Pending Approval</span>
          <span class="chip-val">{{ summary.total_pending }}</span>
        </div>
        <div class="pv-chip pv-chip--blue">
          <span class="chip-label">Approved</span>
          <span class="chip-val">{{ summary.total_approved }}</span>
        </div>
        <div class="pv-chip pv-chip--green">
          <span class="chip-label">Paid</span>
          <span class="chip-val">{{ summary.total_paid }}</span>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="pv-toolbar">
        <div class="pv-tabs">
          <button :class="['pv-tab', statusTab === '' ? 'pv-tab--active' : '']" @click="statusTab = ''">All</button>
          <button :class="['pv-tab', statusTab === 'draft' ? 'pv-tab--active' : '']" @click="statusTab = 'draft'">Draft</button>
          <button :class="['pv-tab', statusTab === 'pending_approval' ? 'pv-tab--active' : '']" @click="statusTab = 'pending_approval'">Pending</button>
          <button :class="['pv-tab', statusTab === 'approved' ? 'pv-tab--active' : '']" @click="statusTab = 'approved'">Approved</button>
          <button :class="['pv-tab', statusTab === 'paid' ? 'pv-tab--active' : '']" @click="statusTab = 'paid'">Paid</button>
        </div>
        <div class="pv-toolbar-right">
          <input v-model="search" class="pv-search" placeholder="Search voucher…" @input="onSearch" />
          <button class="btn-primary" @click="openCreate">+ New Voucher</button>
        </div>
      </div>

      <!-- Table -->
      <div class="table-card">
        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th>PV Number</th>
                <th>Vendor</th>
                <th>Description</th>
                <th>Invoice Date</th>
                <th>Invoice Amount</th>
                <th>Deductions</th>
                <th>Amount Payable</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading"><td colspan="9" class="tbl-loading">Loading…</td></tr>
              <tr v-else-if="!vouchers.data.length"><td colspan="9" class="tbl-empty">No vouchers found</td></tr>
              <tr v-for="v in vouchers.data" :key="v.id" class="data-row">
                <td><span class="pv-ref">{{ v.pv_number }}</span></td>
                <td>{{ v.vendor?.name ?? '—' }}</td>
                <td>{{ v.description }}</td>
                <td>{{ v.invoice_date ?? '—' }}</td>
                <td class="amt">{{ fmt(v.invoice_amount) }}</td>
                <td class="amt">{{ fmt(v.total_deductions) }}</td>
                <td class="amt bold">{{ fmt(v.amount_payable) }}</td>
                <td><span :class="['badge', statusClass(v.status)]">{{ statusLabel(v.status) }}</span></td>
                <td>
                  <button class="btn-link" @click="openView(v.id)">View</button>
                  <button v-if="v.status === 'draft'" class="btn-link btn-link--red" @click="deleteVoucher(v.id)">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Pagination -->
        <div v-if="vouchers.last_page > 1" class="pv-pagination">
          <button :disabled="page <= 1" @click="page--">‹</button>
          <span>{{ page }} / {{ vouchers.last_page }}</span>
          <button :disabled="page >= vouchers.last_page" @click="page++">›</button>
        </div>
      </div>

    </div>

    <!-- Create / View Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-box modal-box--lg">

        <div class="modal-header">
          <h3>{{ modalMode === 'create' ? 'New Payment Voucher' : selected?.pv_number }}</h3>
          <button class="modal-close" @click="showModal = false">×</button>
        </div>

        <!-- VIEW MODE -->
        <div v-if="modalMode === 'view' && selected" class="modal-body">
          <div class="detail-grid">
            <div class="detail-item"><span class="detail-label">PV Number</span><span>{{ selected.pv_number }}</span></div>
            <div class="detail-item"><span class="detail-label">Vendor</span><span>{{ selected.vendor?.name ?? '—' }}</span></div>
            <div class="detail-item"><span class="detail-label">Description</span><span>{{ selected.description }}</span></div>
            <div class="detail-item"><span class="detail-label">Invoice #</span><span>{{ selected.invoice_number ?? '—' }}</span></div>
            <div class="detail-item"><span class="detail-label">Invoice Date</span><span>{{ selected.invoice_date ?? '—' }}</span></div>
            <div class="detail-item"><span class="detail-label">Txn Date</span><span>{{ selected.transaction_date }}</span></div>
            <div class="detail-item"><span class="detail-label">Debit Account</span><span>{{ selected.debit_account?.name ?? '—' }}</span></div>
            <div class="detail-item"><span class="detail-label">Invoice Amount</span><span class="bold">₦{{ fmt(selected.invoice_amount) }}</span></div>
            <div class="detail-item"><span class="detail-label">Deductions</span><span>₦{{ fmt(selected.total_deductions) }}</span></div>
            <div class="detail-item"><span class="detail-label">Amount Payable</span><span class="bold">₦{{ fmt(selected.amount_payable) }}</span></div>
            <div class="detail-item"><span class="detail-label">Status</span><span :class="['badge', statusClass(selected.status)]">{{ statusLabel(selected.status) }}</span></div>
          </div>

          <div v-if="selected.deductions?.length" class="deductions-table">
            <h4>WHT / Deductions</h4>
            <table class="data-table">
              <thead><tr><th>Name</th><th>Rate (%)</th><th>Amount</th></tr></thead>
              <tbody>
                <tr v-for="d in selected.deductions" :key="d.id">
                  <td>{{ d.deduction_name }}</td>
                  <td>{{ d.rate }}%</td>
                  <td class="amt">{{ fmt(d.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="modal-actions">
            <button v-if="selected.status === 'draft'" class="btn-outline" @click="submitForApproval(selected.id)">Submit for Approval</button>
            <button v-if="selected.status === 'pending_approval'" class="btn-primary" @click="approve(selected.id)">Approve</button>
            <button v-if="selected.status === 'approved'" class="btn-success" @click="markPaid(selected.id)">Mark as Paid</button>
          </div>
        </div>

        <!-- CREATE MODE -->
        <div v-else-if="modalMode === 'create'" class="modal-body">
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <div class="form-grid">
            <div class="form-group">
              <label>Vendor</label>
              <select v-model="form.vendor_id">
                <option value="">— Select Vendor —</option>
                <option v-for="vn in vendors" :key="vn.id" :value="vn.id">{{ vn.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>Description *</label>
              <input v-model="form.description" placeholder="Payment description" />
            </div>
            <div class="form-group">
              <label>Invoice Number</label>
              <input v-model="form.invoice_number" placeholder="INV-001" />
            </div>
            <div class="form-group">
              <label>Reference No.</label>
              <input v-model="form.reference_no" />
            </div>
            <div class="form-group">
              <label>Invoice Date</label>
              <input type="date" v-model="form.invoice_date" />
            </div>
            <div class="form-group">
              <label>Transaction Date *</label>
              <input type="date" v-model="form.transaction_date" />
            </div>
            <div class="form-group">
              <label>Debit Account *</label>
              <select v-model="form.debit_account_id">
                <option value="">— Select Account —</option>
                <option v-for="a in accounts" :key="a.id" :value="a.id">{{ a.code }} – {{ a.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>Invoice Amount *</label>
              <input type="number" v-model="form.invoice_amount" min="0" step="0.01" @input="recalcAllDeductions" />
            </div>
            <div class="form-group form-group--full">
              <label>Notes</label>
              <textarea v-model="form.notes" rows="2"></textarea>
            </div>
          </div>

          <!-- Deductions -->
          <div class="deductions-section">
            <div class="deductions-header">
              <h4>Deductions (WHT, etc.)</h4>
              <button class="btn-link" @click="addDeduction">+ Add</button>
            </div>
            <div v-for="(d, i) in form.deductions" :key="i" class="deduction-row">
              <input v-model="d.deduction_name" placeholder="Name (e.g. WHT 5%)" class="ded-name" />
              <input v-model.number="d.rate" type="number" min="0" max="100" step="0.01" placeholder="Rate %" class="ded-rate" @input="recalcDeduction(d)" />
              <input v-model.number="d.amount" type="number" min="0" step="0.01" placeholder="Amount" class="ded-amount" />
              <button class="btn-link btn-link--red" @click="removeDeduction(i)">✕</button>
            </div>
            <div v-if="form.deductions.length" class="deduction-summary">
              Total Deductions: ₦{{ fmt(totalDeductions()) }} &nbsp;|&nbsp; Amount Payable: <strong>₦{{ fmt(amountPayable()) }}</strong>
            </div>
          </div>

          <div class="modal-actions">
            <button class="btn-outline" @click="showModal = false">Cancel</button>
            <button class="btn-primary" :disabled="saving" @click="saveVoucher">{{ saving ? 'Saving…' : 'Create Voucher' }}</button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
.pv-page { padding: 0 24px 40px; }
.pv-chips { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
.pv-chip { display: flex; flex-direction: column; align-items: center; padding: 12px 20px; border-radius: 8px; min-width: 110px; }
.pv-chip--grey   { background: #f5f5f5; }
.pv-chip--orange { background: #fff8ee; }
.pv-chip--blue   { background: #eef4ff; }
.pv-chip--green  { background: #edf7f2; }
.chip-label { font-size: 12px; color: #888; margin-bottom: 4px; }
.chip-val   { font-size: 22px; font-weight: 700; color: #1a2a45; }

.pv-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; gap: 12px; flex-wrap: wrap; }
.pv-tabs    { display: flex; gap: 4px; }
.pv-tab     { padding: 7px 14px; border: 1px solid #dde3ef; border-radius: 6px; background: #fff; font-size: 13px; cursor: pointer; color: #555; }
.pv-tab--active { background: #004AAD; color: #fff; border-color: #004AAD; }
.pv-toolbar-right { display: flex; gap: 10px; align-items: center; }
.pv-search  { padding: 8px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13px; width: 220px; }

.table-card  { background: #fff; border-radius: 10px; border: 1px solid #eaf0fb; overflow: hidden; }
.table-scroll { overflow-x: auto; }
.data-table  { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.data-table th { padding: 12px 14px; text-align: left; background: #f7f9fc; font-weight: 600; color: #556; font-size: 12px; white-space: nowrap; }
.data-table td { padding: 12px 14px; border-top: 1px solid #f0f3fa; vertical-align: middle; }
.data-row:hover td { background: #fafbff; }
.tbl-loading, .tbl-empty { text-align: center; color: #999; padding: 40px; }
.amt  { text-align: right; font-variant-numeric: tabular-nums; }
.bold { font-weight: 600; }
.pv-ref { font-weight: 600; color: #004AAD; }

.pv-pagination { display: flex; align-items: center; gap: 12px; justify-content: center; padding: 12px; }
.pv-pagination button { padding: 5px 12px; border: 1px solid #dde3ef; border-radius: 4px; cursor: pointer; background: #fff; }
.pv-pagination button:disabled { opacity: 0.4; cursor: not-allowed; }

.badge          { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; }
.badge--grey    { background: #f0f0f0; color: #666; }
.badge--orange  { background: #fff3cd; color: #856404; }
.badge--blue    { background: #cfe2ff; color: #084298; }
.badge--green   { background: #d1e7dd; color: #0f5132; }
.badge--red     { background: #f8d7da; color: #842029; }

.btn-primary  { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-outline  { padding: 9px 20px; background: #fff; color: #004AAD; border: 1px solid #004AAD; border-radius: 6px; font-size: 13.5px; cursor: pointer; }
.btn-success  { padding: 9px 20px; background: #198754; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-link     { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; padding: 0 4px; }
.btn-link--red{ color: #dc3545; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding-top: 60px; }
.modal-box     { background: #fff; border-radius: 12px; width: 680px; max-width: 95vw; max-height: 85vh; overflow-y: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18); }
.modal-box--lg { width: 760px; }
.modal-header  { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eaf0fb; }
.modal-header h3 { font-size: 16px; font-weight: 700; color: #1a2a45; margin: 0; }
.modal-close   { background: none; border: none; font-size: 22px; color: #888; cursor: pointer; }
.modal-body    { padding: 20px 24px 24px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }

.form-grid     { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 18px; }
.form-group    { display: flex; flex-direction: column; gap: 5px; }
.form-group--full { grid-column: 1 / -1; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group input, .form-group select, .form-group textarea {
  padding: 9px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; color: #1a2a45;
}
.form-error { background: #f8d7da; color: #842029; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 14px; }

.detail-grid  { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 18px; margin-bottom: 20px; }
.detail-item  { display: flex; flex-direction: column; gap: 3px; }
.detail-label { font-size: 11.5px; color: #888; font-weight: 600; }

.deductions-section { margin-top: 20px; border-top: 1px solid #eaf0fb; padding-top: 16px; }
.deductions-header  { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.deductions-header h4 { font-size: 13.5px; font-weight: 700; color: #1a2a45; margin: 0; }
.deduction-row { display: flex; gap: 8px; margin-bottom: 8px; align-items: center; }
.ded-name   { flex: 2; padding: 7px 10px; border: 1px solid #dde3ef; border-radius: 5px; font-size: 13px; }
.ded-rate   { flex: 1; padding: 7px 10px; border: 1px solid #dde3ef; border-radius: 5px; font-size: 13px; }
.ded-amount { flex: 1; padding: 7px 10px; border: 1px solid #dde3ef; border-radius: 5px; font-size: 13px; }
.deduction-summary { font-size: 13px; color: #556; text-align: right; margin-top: 8px; }
.deductions-table { margin-top: 16px; }
.deductions-table h4 { font-size: 13.5px; font-weight: 700; margin-bottom: 8px; }
</style>
